/**
 * MediaPipe Pose Overlay Configuration
 * Controls visual appearance, smoothing, and rendering parameters
 */

export const overlayConfig = {
  // Smoothing filter selection
  filter: 'oneEuro' as const,      // 'ema' | 'oneEuro'
  
  // Exponential Moving Average settings
  ema: { 
    alpha: 0.35 
  },
  
  // One-Euro Filter settings (dt-aware, better for variable frame rates)
  oneEuro: { 
    minCutoff: 1.3,    // Higher = more smoothing at low speeds
    beta: 0.007,       // Higher = more responsive to acceleration  
    dCutoff: 1.0       // Smoothing for derivative calculation
  },
  
  // Outlier rejection and interpolation
  outlier: { 
    sigma: 3.0,        // Standard deviations for outlier detection
    maxJumpPx: 40      // Maximum pixel jump before considering outlier
  },
  
  // Visual styling (base values, scaled by body size)
  style: {
    baseStrokePx: 2.2,            // INCREASED for visibility - will be scaled by bodyScalePx
    baseJointPx: 4.2,             // INCREASED - base joint/dot radius
    strokeAlpha: 0.85,            // INCREASED - less faint, more visible
    jointAlpha: 0.85,             // High visibility for dots
    shadowBlur: 2,                // Subtle shadow for depth
    shadowAlpha: 0.3
  },
  
  // Body-proportional normalization
  normalization: {
    scaleAnchor: 'shoulder' as const, // 'shoulder' | 'hip' 
    fallbackAnchor: 'hip' as const,   // Fallback if shoulder not visible
    minStrokePx: 1.6,                 // INCREASED minimum stroke width
    maxStrokePx: 4.8,                 // INCREASED maximum stroke width  
    minJointPx: 3.5,                  // INCREASED minimum joint radius
    maxJointPx: 10,                   // Maximum joint radius
    baseBodyScalePx: 120              // Reference body scale (shoulder width)
  },
  
  // MediaPipe landmark indices
  landmarks: {
    pose: {
      NOSE: 0,
      LEFT_SHOULDER: 11, RIGHT_SHOULDER: 12,
      LEFT_HIP: 23, RIGHT_HIP: 24,
      LEFT_ELBOW: 13, RIGHT_ELBOW: 14,
      LEFT_WRIST: 15, RIGHT_WRIST: 16,
      LEFT_KNEE: 25, RIGHT_KNEE: 26,
      LEFT_ANKLE: 27, RIGHT_ANKLE: 28
    }
  },
  
  // Skeleton connections with depth ordering (back to front)
  connections: {
    // Torso (base layer)
    torso: [
      [11, 12], // shoulders
      [11, 23], [12, 24], // shoulder to hip
      [23, 24]  // hips
    ],
    
    // Arms (left = far in golf stance, right = near)
    leftArm: [
      [11, 13], [13, 15], // shoulder -> elbow -> wrist
      [15, 17], [15, 19], [15, 21] // wrist details
    ],
    
    rightArm: [
      [12, 14], [14, 16], // shoulder -> elbow -> wrist  
      [16, 18], [16, 20], [16, 22] // wrist details
    ],
    
    // Legs (left = far in golf stance, right = near)
    leftLeg: [
      [23, 25], [25, 27], // hip -> knee -> ankle
      [27, 29], [27, 31], [29, 31] // foot details
    ],
    
    rightLeg: [
      [24, 26], [26, 28], // hip -> knee -> ankle
      [28, 30], [28, 32], [30, 32] // foot details  
    ]
  },
  
  // Colors for different body parts - HIGHER CONTRAST
  colors: {
    torso: '#7bdcff',         // Bright cyan - high contrast
    leftArm: '#b28cff',       // Purple (far side) 
    rightArm: '#7ee787',      // Bright green (near side)
    leftLeg: '#ffb86b',       // Orange (far side)
    rightLeg: '#ff7b7b',      // Red (near side)
    joints: 'rgba(255, 255, 255, 0.95)',     // Bright white dots
    hands: {
      left: '#7bdcff',        // Cyan
      right: '#ff7b7b'        // Red
    }
  },
  
  // Advanced jitter reduction settings
  jitter: {
    warmup: { frames: 8, rampMs: 250 },
    endcap: { precomputedBiDirectional: true, frames: 12 },
    stillness: {
      anchors: ['leftShoulder', 'rightShoulder', 'leftHip', 'rightHip'],
      medianWindow: 7,
      vThreshPxPerS: 6,
      hysteresisMs: 150,
      minVisibility: 0.5,
      deltas: { minCutoff: 0.6, beta: -0.003 } // tighter smoothing when still
    },
    dtClamp: { min: 1/120, max: 1/20 }, // 8.33ms to 50ms
    perJoint: { wristMult: 1.25, ankleMult: 1.15 }, // extra smoothing for problem joints
    legacy: {
      resampleToFps: 60,           // resample irregular legacy data
      enableBidirectional: true,   // forward+backward pass for precomputed
      warmupFrames: 12             // extra warmup for legacy data
    }
  },

  // Development debug overlay
  debug: { 
    showBounds: false,     // Show video content bounds
    showScale: false,      // Show body scale calculation
    showFPS: true,         // Show rendering FPS
    showTiming: false,     // Show frame timing info
    showJitter: false,     // Show jitter reduction metrics
    showPhase: false,      // Show warm-up/still/motion phase
    exportCSV: false       // Export frame data for analysis
  }
};

export type OverlayConfig = typeof overlayConfig;

// URL parameter overrides for field tuning
export function getConfigWithOverrides(config: OverlayConfig = overlayConfig): OverlayConfig {
  if (typeof window === 'undefined') return config;
  
  const params = new URLSearchParams(window.location.search);
  const overridden = { ...config };
  
  // One-Euro parameters
  if (params.has('beta')) {
    overridden.oneEuro.beta = parseFloat(params.get('beta')!);
  }
  if (params.has('minCutoff')) {
    overridden.oneEuro.minCutoff = parseFloat(params.get('minCutoff')!);
  }
  if (params.has('dCutoff')) {
    overridden.oneEuro.dCutoff = parseFloat(params.get('dCutoff')!);
  }
  
  // Visual parameters
  if (params.has('stroke')) {
    overridden.style.baseStrokePx = parseFloat(params.get('stroke')!);
  }
  if (params.has('joint')) {
    overridden.style.baseJointPx = parseFloat(params.get('joint')!);
  }
  
  // Debug flags
  if (params.has('debug')) {
    overridden.debug.showBounds = true;
    overridden.debug.showScale = true;
    overridden.debug.showFPS = true;
  }
  
  return overridden;
}
